import { RouteFactory } from "@app/routePaths";
import logoBT from "@assets/logo-masthead.svg";
import logoNUS from "@assets/logo-nus.png";
import logoUOB from "@assets/logo-uob-blue.png";
import { BlockTitle } from "@blocks/Blocks";
import AccordionV2 from "@components/Accordion/AccordionV2";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import EventsAwardsAccordionHeader from "@events-awards/components/EventsAwardsAccordionHeader";

/**
 * Component to render SIA partners page
 */
const SIAPartnersPage = (): React.ReactElement => {
  return (
    <div data-testid="sia-partners-page-component" className="font-public-sans">
      <BlockTitle
        text="Partners"
        rootClassName="my-3 text-3xl md:text-5xl lg:text-6xl font-medium font-public-sans"
      />
      <Column rootClassName="text-left justify-center mb-12">
        <p className="text-center">Co-organised By</p>

        <Row rootClassName="justify-center items-center mb-10">
          <Column>
            <img
              src={logoBT}
              width={211}
              alt="The Business Times logo"
              className="my-6 md:mx-16"
            />
          </Column>

          <Column>
            <img
              src={logoUOB}
              width={141}
              alt="UOB logo"
              className="mx-16 my-6"
            />
          </Column>
        </Row>

        <p className="text-center">Knowledge partner</p>

        <div className="flex flex-col items-center">
          <img src={logoNUS} width={366} alt="NUS logo" />
        </div>
      </Column>

      <AccordionV2
        testId="event-awards-sia-business-times-accordion"
        className="rounded-tl-lg rounded-tr-lg border border-gray-175 bg-gray-150 py-4"
        headerChildren={
          <EventsAwardsAccordionHeader title="The Business Times" />
        }
        arrowClassName="border-none h-auto w-auto pr-6"
      >
        <p className="mt-4 border-t border-gray-175 px-6 pt-4">
          The Business Times, published by SPH Media Trust, is South-east
          Asia&apos;s leading financial daily. BT brings to its readers daily
          corporate, financial, economic and political news, analysis and
          commentary on print and digital platforms. It provides readers with
          in-depth coverage of Singapore and Asian business and economic
          developments, as well as global trends that impact Singapore business.
          BT&apos;s lifestyle journalists bring busy executives the latest in
          recreation, entertainment, the arts, design, food and shopping. For
          more information, please visit{" "}
          <a
            href={RouteFactory.home}
            className="font-bold text-blue-175 hover:text-gray-750"
          >
            businesstimes.com.sg
          </a>
          .
        </p>
      </AccordionV2>

      <AccordionV2
        testId="event-awards-sia-uob-accordion"
        className="border-x border-gray-175 bg-gray-150 py-4"
        headerChildren={<EventsAwardsAccordionHeader title="UOB" />}
        arrowClassName="border-none h-auto w-auto pr-6"
      >
        <p className="mt-4 border-t border-gray-175 px-6 pt-4">
          {" "}
          UOB is a leading bank in Asia with a global network of around 500
          offices in 19 countries and territories in Asia Pacific, Europe and
          North America. Since its incorporation in 1935, UOB has grown
          organically and through a series of strategic acquisitions. UOB is
          rated among the world&apos;s top banks: Aa1 by Moody&apos;s Investors
          Service and AA- by both S&P Global Ratings and Fitch Ratings. In Asia,
          UOB operates through its head office in Singapore and banking
          subsidiaries in China, Indonesia, Malaysia, Thailand and Vietnam, as
          well as branches and representative offices across the region. Over
          more than eight decades, generations of UOB employees have carried
          through the entrepreneurial spirit, the focus on long-term value
          creation and an unwavering commitment to do what is right for our
          customers and our colleagues. We believe in being a responsible
          financial services provider and we are committed to making a
          difference in the lives of our stakeholders and in the communities in
          which we operate. Just as we are dedicated to helping our customers
          manage their finances wisely and to grow their businesses, UOB is
          steadfast in our support of social development, particularly in the
          areas of art, children and education.
        </p>
      </AccordionV2>

      <AccordionV2
        testId="event-awards-sia-nus-accordion"
        className="rounded-bl-lg rounded-br-lg border border-t-0 border-gray-175 bg-gray-150 py-4"
        headerChildren={
          <EventsAwardsAccordionHeader title="NUS Business School - Centre for Governance and Sustainability" />
        }
        arrowClassName="border-none h-auto w-auto pr-6"
      >
        <p className="mt-4 border-t border-gray-175 px-6 pt-4">
          The Centre for Governance and Sustainability (CGS), formerly known as
          Centre for Governance, Institutions and Organisations (CGIO), was
          established by the National University of Singapore (NUS) Business
          School in 2010. It aims to spearhead relevant and high-impact research
          on corporate governance and corporate sustainability issues that are
          pertinent to institutions, government bodies and businesses both in
          Singapore and Asia. This includes corporate governance and corporate
          sustainability, governance of family firms, government-linked
          companies, business groups, and institutions. CGS also organises
          events such as public lectures, industry roundtables, and academic
          conferences on topics related to governance and sustainability.
        </p>
        <p className="px-6">
          CGS is the national assessor for the corporate sustainability and
          corporate governance performance of listed companies in Singapore.
          More information about CGS can be accessed at{" "}
          <a
            className="font-bold text-blue-175 hover:text-gray-750"
            href="https://bschool.nus.edu.sg/cgs/"
          >
            https://bschool.nus.edu.sg/cgs/
          </a>
        </p>
        <p className="px-6">
          Founded in the same year that Singapore gained independence, NUS
          Business School stands among the world&apos;s leading business schools
          today. It is distinctive for offering the best of global business
          knowledge with deep Asian insights, preparing students to lead Asian
          businesses to international success and to help global businesses
          succeed in Asia. The School attracts a diversity of smart and talented
          students to our broad portfolio of academic programmes, including BBA,
          MBA, Executive MBA, MSc and PhD programmes in addition to our
          customised and open enrolment Executive Education courses. Admission
          to NUS Business School is highly competitive, and we are proud of the
          exceptional quality of our students. For more information, please
          visit{" "}
          <a
            className="font-bold text-blue-175 hover:text-gray-750"
            href="https://bschool.nus.edu.sg/"
          >
            https://bschool.nus.edu.sg/
          </a>
          .
        </p>
      </AccordionV2>
    </div>
  );
};
export default SIAPartnersPage;
